import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

import TraceStepRow from '~tread-agent/trace/TraceStepRow';
import { liveLineFrames } from '~tread-agent/trace/traceUtils';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import { TreadAgentTraceItem } from '~tread-agent/types';

const LOADER_SIZE_IN_PX = 18;
// Reading runs at roughly 20 characters a second, so a frame earns its dwell by length — but
// never less than long enough to register, and never so long the line feels stuck.
const MIN_DWELL_MS = 1200;
const MAX_DWELL_MS = 3000;
const MS_PER_CHARACTER = 30;

function dwellFor(text: string) {
  return Math.min(MAX_DWELL_MS, Math.max(MIN_DWELL_MS, text.length * MS_PER_CHARACTER));
}

const SWAP_DURATION_MS = 150;
const SWAP_DELAY_MS = 50;

const swapIn = keyframes`
  from { opacity: 0; filter: blur(2px); transform: translateY(8px); }
  to { opacity: 1; filter: blur(0); transform: none; }
`;

function useLiveFrame(trace: TreadAgentTraceItem[]) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const frames = useMemo(() => liveLineFrames(trace), [trace]);
  const newestIndex = Math.max(0, frames.length - 1);
  const [index, setIndex] = useState(newestIndex);
  // A new turn clears the trace, which would otherwise leave the index past its end.
  const shownIndex = Math.min(index, newestIndex);
  const frame = frames[shownIndex] ?? null;
  const hasNewerFrame = shownIndex < newestIndex;
  const dwellMs = prefersReducedMotion
    ? 0
    : dwellFor(
        frame?.kind === 'step' ? (frame.step.activity ?? '') : (frame?.text ?? ''),
      );
  const newestIndexRef = useRef(newestIndex);
  newestIndexRef.current = newestIndex;

  useEffect(() => {
    if (!hasNewerFrame) {
      return;
    }

    const timeoutId = setTimeout(() => setIndex(newestIndexRef.current), dwellMs);

    return () => clearTimeout(timeoutId);
  }, [hasNewerFrame, shownIndex, dwellMs]);

  return frame;
}

export default function TurnTraceLive({
  elapsedLabel,
  trace,
}: {
  elapsedLabel: string;
  trace: TreadAgentTraceItem[];
}) {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const frame = useLiveFrame(trace);
  const labelSx = {
    color: theme.brandV2.colors.treadGray1,
    fontSize: '12px',
    fontWeight: 500,
  };

  return (
    <Box
      data-test-id="tread-agent-turn-trace-live"
      sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0, width: '100%' }}
    >
      <TreadAgentLoader size={LOADER_SIZE_IN_PX} />

      <Box
        // Keyed by what is showing, so React replaces the node and the swap animation replays.
        key={frame?.id ?? 'thinking'}
        sx={{
          minWidth: 0,
          ...(prefersReducedMotion
            ? {}
            : {
                animation: `${swapIn} ${SWAP_DURATION_MS}ms ease-in-out ${SWAP_DELAY_MS}ms both`,
              }),
        }}
      >
        {frame === null && (
          <Typography data-test-id="tread-agent-trace-live-thinking" sx={labelSx}>
            {t('tread_agent.trace.thinking_placeholder')}
          </Typography>
        )}

        {frame?.kind === 'step' && <TraceStepRow step={frame.step} />}

        {frame?.kind === 'reasoning' && (
          <Typography
            data-test-id="tread-agent-trace-live-reasoning"
            sx={{
              ...labelSx,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {frame.text}
          </Typography>
        )}
      </Box>

      <Typography
        sx={{
          color: theme.brandV2.colors.treadGray3,
          flexShrink: 0,
          fontSize: '12px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {elapsedLabel}
      </Typography>
    </Box>
  );
}
